import type { ISubscription } from "@/services/SubscriptionService";
import type { TRenewalOptionsDTO, TSubscriptionOptionDTO } from "@/services/UsersSubscriptionService";

// Lógica pura sobre la respuesta de GET subscriptionOptions/{usersSubscriptionId}. Las reglas de tarifa
// (qué cabe, cuál es la requerida, cuánto se cobra) las decide el backend; aquí solo se interpreta.

// Type de las tarifas promocionales en BD (SubscriptionOptionRules.PromotionType en el backend).
export const PromotionSubscriptionType = "promotion";

/**
 * True si la tarifa es una promoción. Mismo criterio que SubscriptionOptionRules.IsType del backend:
 * se ignoran los espacios y las mayúsculas.
 */
export const isPromotionSubscriptionType = (type: string | null | undefined): boolean =>
{
    return (type ?? "").trim().toLowerCase() === PromotionSubscriptionType;
};

/**
 * Recargo por caducidad que se muestra (y que limita los Elements) al renovar.
 * Una promoción no se renueva como promoción: pasa a experience con precio íntegro y sin recargo,
 * igual que cobra el backend. En el resto de tarifas se mantiene el recargo calculado.
 */
export const resolveRenewalSurcharge = (currentSubscriptionType: string | null | undefined, computedSurcharge: number): number =>
{
    return isPromotionSubscriptionType(currentSubscriptionType) ? 0 : computedSurcharge;
};

/**
 * Convierte una opción del backend al ISubscription que usan los selectores visuales.
 * La descripción no viene en el endpoint: se puede completar con la del catálogo (solo texto).
 * limit null (o 0) = sin límite, que en ISubscription se representa sin limit.
 */
export const toSubscriptionFromOption = (option: TSubscriptionOptionDTO, description?: string): ISubscription =>
({
    id: option.id,
    name: option.name,
    description: description ?? "",
    period: option.period,
    price: option.price,
    limit: option.limit ? option.limit : undefined,
    type: option.type,
    subscriptionType: option.type,
    priceIronia: option.price,
    priceProvider: 0,
    riskLevel: 0,
});

/**
 * Orden de presentación en la gráfica de planes: límite ascendente y "sin límite" al final.
 * Es solo visual (las opciones válidas las decide el backend); a igual límite se respeta el orden recibido.
 */
export const sortOptionsForChart = <T extends TSubscriptionOptionDTO>(options: T[]): T[] =>
{
    const limitValue = (limit: number | null) => (limit ? limit : Infinity);
    return options
        .map((option, index) => ({ option, index }))
        .sort((a, b) =>
        {
            const limitA = limitValue(a.option.limit);
            const limitB = limitValue(b.option.limit);
            if(limitA !== limitB)
            {
                return limitA < limitB ? -1 : 1;
            }

            return a.index - b.index;
        })
        .map((item) => item.option);
};

const containsOption =(options: { id: number }[] | null | undefined, id: number | null | undefined): boolean =>
{
    return id !== null && id !== undefined && (options ?? []).some((option) => option.id === id);
};

/**
 * Tarifa preseleccionada en "Más planes" de renovación: la que hubiera elegido el usuario si sigue entre
 * las opciones; si no, la requerida; si no, la actual si es opción; si no, la primera. undefined sin opciones.
 */
export const resolveRenewalPreselectedId = (renewal: TRenewalOptionsDTO | null | undefined, userChosenId: number | null | undefined, currentSubscriptionId: number | null | undefined): number | undefined =>
{
    const options = renewal?.options ?? [];
    if(options.length === 0)
    {
        return undefined;
    }

    if(containsOption(options, userChosenId))
    {
        return userChosenId as number;
    }

    if(containsOption(options, renewal?.requiredSubscriptionId))
    {
        return renewal?.requiredSubscriptionId as number;
    }

    if(containsOption(options, currentSubscriptionId))
    {
        return currentSubscriptionId as number;
    }

    return options[0].id;
};

export type TRenewalTarget =
{
    // Tarifa a renovar que debe usar RePaymentSubscription (undefined = la actual de los contratos).
    subscriptionId: number | undefined;
    // true si se ha sustituido la tarifa por la requerida y hay que mostrar renewal.message.
    mustShowRequiredMessage: boolean;
};

/**
 * Tarifa que se va a renovar en RePaymentSubscription. Si el backend exige una tarifa
 * (requiredSubscriptionId) y la que se iba a renovar no es una opción válida, se pasa a la requerida y se
 * avisa. Si la elegida sí es una opción válida (p. ej. una superior elegida en "Más planes"), se respeta.
 */
export const resolveRenewalTarget = (renewal: TRenewalOptionsDTO | null | undefined, chosenSubscriptionId: number | undefined, currentSubscriptionId: number | undefined): TRenewalTarget =>
{
    const unchanged: TRenewalTarget = { subscriptionId: chosenSubscriptionId, mustShowRequiredMessage: false };
    if(!renewal || renewal.allowed !== true)
    {
        return unchanged;
    }

    const required = renewal.requiredSubscriptionId;
    const target = chosenSubscriptionId ?? currentSubscriptionId;
    if(required === null || required === undefined || required === target || containsOption(renewal.options, target))
    {
        return unchanged;
    }

    return { subscriptionId: required, mustShowRequiredMessage: true };
};
